<template>
  <BaseCard type="filled" class="flex flex-row items-center p-3">
    <div class="relative mr-4 shrink-0">
      <BaseShapeMask shape="9-sided-cookie" :scale="1.1" class="relative z-10" animation="spin">
        <BaseMemberAvatar :member="receiver" size="64" />
      </BaseShapeMask>
    </div>
    <div class="flex min-w-0 shrink grow flex-col">
      <p class="text-body-medium text-on-surface-variant mb-0.5">
        Feedbackanfrage · <TimeAgo :date="task.createdAt" />
      </p>
      <h3 class="text-title-large">{{ receiverName }}</h3>
      <p v-if="plainText" class="text-body-medium text-on-surface-variant mt-1 italic">
        {{ plainText }}
      </p>
      <div class="mt-1.5 flex flex-row items-center">
        <BaseCircularProgressIndictator
          class="mr-1 shrink-0"
          :percentage="(numberOfGivenFeedbacks / task.numberOfQuestions) * 100"
          :radius="10"
          :thickness="4"
        />
        <span
          class="text-body-medium text-on-surface-variant overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ task.numberOfQuestions }} {{ $t('common.question', task.numberOfQuestions) }}
          {{ $t('common.estimatedTime', { count: task.timeEstimate }) }} ·
          {{ feedbackTopicTitles }}</span
        >
      </div>
    </div>
    <BaseButtonGroup class="ml-2 shrink-0 self-center">
      <BaseMenu v-if="accepted" :items="menuItems" v-slot="{ open }">
        <BaseIconButton icon="more_vert" size="M" width="narrow" :force-pressed="open" />
      </BaseMenu>
      <BaseIconButton
        v-else
        icon="close"
        size="M"
        color="outlined"
        width="narrow"
        @click="openDialog"
      />
      <BaseTextButton size="M" color="tonalPrimary" @click="accept" width="wide"
        >Starten</BaseTextButton
      >
    </BaseButtonGroup>
    <BaseDialog
      type="basic"
      :is-open="isOpen"
      @close="closeDialog"
      title="Anfrage ablehnen"
      description="Möchtest du die Feedbackanfrage wirklich ablehnen?"
    >
      <BaseInputField
        class="bg-surface-container-high mt-2"
        label="Begründung"
        type="multiText"
        v-model="rejectionReason"
      />
      <template #confirm
        ><BaseTextButton @click="clickReject">Anfrage ablehnen</BaseTextButton></template
      >
      <template #dismiss
        ><BaseTextButton @click="closeDialog">{{ $t('common.cancel') }}</BaseTextButton></template
      >
    </BaseDialog>
  </BaseCard>
</template>

<script setup lang="ts">
  import { useWysiwygEditor } from '@/core/composables/useWysiwygEditor'
  import { computed, ref } from 'vue'
  import BaseMemberAvatar from '../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import TimeAgo from '../../../core/components/base/time-ago/TimeAgo.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import BaseIconButton from '../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseShapeMask from '../../../core/components/base/shapes/BaseShapeMask.vue'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '../base/dialog/use-dialog'
  import BaseInputField from '../../../core/components/base/form-fields/BaseInputField.vue'
  import { useMutateFeedbackRequest } from '../../composables/use-mutate-feedback-request'
  import type { FeedbackTask } from '@/modules/task/composables/useFeedbackTasks'
  import BaseCircularProgressIndictator from '../base/circlular-progress-indicator/BaseCircularProgressIndictator.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import { useLocalFeedbacks } from '@/core/composables/useLocalFeedbacks'
  import { useI18n } from 'vue-i18n'
  import BaseCard from '../base/card/BaseCard.vue'
  import { OpenFeedbackState } from '@/modules/open-feedbacks/composables/useOpenFeedbacks'
  import BaseButtonGroup from '../base/button/BaseButtonGroup.vue'
  import { memberFullName } from '@/core/models/member/member-name'

  const { task } = defineProps<{
    task: FeedbackTask
  }>()

  const receiver = computed(() => {
    return surveyTandem.value.receiver
  })

  const surveyTandem = computed(() => {
    return task.surveyTandems[0]
  })

  const accepted = computed(() => {
    return surveyTandem.value.senderState === OpenFeedbackState.InProgress
  })

  const feedbackTopicTitles = computed(() => {
    return task.feedbackTopics.map((topic) => topic.title).join(', ')
  })

  const { accept, reject } = useMutateFeedbackRequest({
    feedbackReceiverId: receiver.value.id,
    surveyId: task.surveyId,
  })

  const clickReject = () => {
    reject(rejectionReason.value)
    closeDialog()
  }

  const { isOpen, openDialog, closeDialog } = useDialog()

  const rejectionReason = ref<string | undefined>(undefined)

  const { convertJSONToPlainText } = useWysiwygEditor()

  const plainText = computed(() => {
    if (!task.message) return undefined

    const jsonPlainText = convertJSONToPlainText(task.message)

    if (!jsonPlainText) return undefined

    return jsonPlainText
  })

  const receiverName = computed(() => memberFullName(receiver.value))

  const { feedbacksBySurveyIdAndReceiverId } = useLocalFeedbacks()
  const feedbacks = feedbacksBySurveyIdAndReceiverId(task.surveyId, receiver.value.id)

  const numberOfGivenFeedbacks = computed(() => {
    return feedbacks.value.length
  })

  const { t } = useI18n()

  const menuItems = computed(() => {
    return [
      {
        id: 'delete',
        title: t('common.cancel'),
        leadingIcon: 'close',
        callback: () => {
          openDialog()
        },
      },
    ]
  })
</script>
